import { useState } from 'react';
import { Copy, Check, MessageCircle, Facebook, Linkedin, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { openSms, openExternal, copyWithToast } from '@/lib/shareLaunch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { MOOD_OPTIONS } from '@/types/mood';
import { useLanguage } from '@/i18n/LanguageContext';

interface MoodShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moodValue: number;
  moodLabel: string;
  notes?: string;
  createdAt: string;
}

export function MoodShareDialog({
  open,
  onOpenChange,
  moodValue,
  moodLabel,
  notes,
  createdAt,
}: MoodShareDialogProps) {
  const { language } = useLanguage();
  const [copied, setCopied] = useState(false);

  const moodOption = MOOD_OPTIONS.find((m) => m.value === moodValue);
  const emoji = moodOption?.emoji || '😐';
  
  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  // Create the share message with branding
  const createShareMessage = () => {
    let message = `${emoji} I'm feeling ${moodLabel} right now.`;
    if (notes) {
      message += `\n\n"${notes}"`;
    }
    message += `\n\n— Shared from Say It NOW\n🌐 say-it-now.app`;
    return message;
  };

  const shareMessage = createShareMessage();

  const content = {
    en: {
      title: 'Send Your Mood',
      description: 'Let someone know how you\'re feeling right now',
      preview: 'Message Preview',
      copyMessage: 'Copy Message',
      sendVia: 'Send via',
      sms: 'Text Message',
      whatsapp: 'WhatsApp',
      email: 'Email',
      copied: 'Message copied!',
    },
    es: {
      title: 'Enviar Tu Estado',
      description: 'Hazle saber a alguien cómo te sientes ahora',
      preview: 'Vista Previa',
      copyMessage: 'Copiar Mensaje',
      sendVia: 'Enviar por',
      sms: 'Mensaje de Texto',
      whatsapp: 'WhatsApp',
      email: 'Correo',
      copied: '¡Mensaje copiado!',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const handleCopy = async () => {
    const ok = await copyWithToast(shareMessage, t.copied, 'Failed to copy');
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSMS = () => {
    openSms(shareMessage);
  };

  const handleWhatsApp = () => {
    openExternal(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`);
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`I wanted to share how I'm feeling`);
    const body = encodeURIComponent(shareMessage);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleFacebook = () => {
    openExternal(`https://www.facebook.com/sharer/sharer.php?quote=${encodeURIComponent(shareMessage)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5 text-primary" />
            {t.title}
          </DialogTitle>
          <DialogDescription>{t.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Message Preview */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">{t.preview}</label>
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">{emoji}</span>
                <span className="font-medium text-foreground">I'm feeling {moodLabel}</span>
              </div>
              {notes && (
                <p className="text-sm text-muted-foreground italic mb-3">"{notes}"</p>
              )}
              <div className="pt-3 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  — Shared from <span className="font-medium text-primary">Say It NOW</span>
                </p>
                <p className="text-xs text-muted-foreground">🌐 say-it-now.app</p>
              </div>
            </div>
          </div>

          {/* Copy Message */}
          <div className="space-y-2">
            <Button onClick={handleCopy} variant="outline" className="w-full gap-2">
              {copied ? (
                <>
                  <Check className="h-4 w-4 text-primary" />
                  {t.copied}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  {t.copyMessage}
                </>
              )}
            </Button>
          </div>

          {/* Send Options */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">{t.sendVia}</label>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={handleSMS} variant="outline" className="gap-2">
                <MessageCircle className="h-4 w-4" />
                {t.sms}
              </Button>
              <Button onClick={handleWhatsApp} variant="outline" className="gap-2">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {t.whatsapp}
              </Button>
              <Button onClick={handleEmail} variant="outline" className="gap-2">
                <Mail className="h-4 w-4" />
                {t.email}
              </Button>
              <Button onClick={handleFacebook} variant="outline" className="gap-2">
                <Facebook className="h-4 w-4" />
                Facebook
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
